import React from 'react';
import { FlexDiv, TextDefault, TextInput } from './ui';
import { Paper, Stack, TextField, Checkbox, IconButton, MenuItem, FormControl, InputLabel, Select } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

const LINK_TYPES = ['button', 'link'];

/**
 * Extra fields for `presentation.type === "custom"` (62.json, 77.json).
 */
export function CustomPresentationEditor({
    presentation,
    onUpdatePresentation,
}) {
    if (presentation?.type !== 'custom') {
        return null;
    }

    const links = Array.isArray(presentation.links) ? presentation.links : [];

    const patchLinks = (next) => {
        onUpdatePresentation({ links: next });
    };

    const patchLink = (index, patch) => {
        const next = links.map((row, i) =>
            i === index ? { ...row, ...patch } : row
        );
        patchLinks(next);
    };

    const addLink = () => {
        patchLinks([
            ...links,
            {
                href: '',
                text: '',
                type: 'button',
                clickActionId: `action_${Date.now()}`,
            },
        ]);
    };

    const removeLink = (index) => {
        patchLinks(links.filter((_, i) => i !== index));
    };

    return (
        <FlexDiv column gap={12}>
            <TextDefault bold>Custom presentation</TextDefault>
            <TextInput
                label="Description"
                value={presentation.description || ''}
                onChange={(e) =>
                    onUpdatePresentation({ description: e.target.value })
                }
                placeholder="Plain text or i18n key"
            />
            <TextInput
                label="Variant"
                value={presentation.variant || ''}
                onChange={(e) => onUpdatePresentation({ variant: e.target.value })}
                placeholder="installPlugin | ransomwareAttack | …"
            />
            <FlexDiv alignCenter gap={8}>
                <Checkbox
                    checked={Boolean(presentation.fullScreen)}
                    onChange={(e) =>
                        onUpdatePresentation({ fullScreen: e.target.checked })
                    }
                />
                <TextDefault>Full screen</TextDefault>
            </FlexDiv>
            <TextField
                multiline
                minRows={2}
                fullWidth
                label="Content (optional)"
                value={presentation.content || ''}
                onChange={(e) =>
                    onUpdatePresentation({ content: e.target.value })
                }
            />

            <TextDefault bold>Links</TextDefault>
            <TextDefault lighter>
                Buttons or links can call a clickActionId; scores are edited under
                “Presentation interaction scores” when the action uses an array
                entry in question.actions.
            </TextDefault>
            {links.map((link, index) => (
                <Paper
                    key={index}
                    elevation={0}
                    sx={{
                        p: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                    }}
                >
                    <Stack spacing={1.5}>
                        <FlexDiv alignCenter gap={8}>
                            <TextDefault bold>Link {index + 1}</TextDefault>
                            <IconButton
                                size="small"
                                onClick={() => removeLink(index)}
                                aria-label="Remove link"
                            >
                                <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                        </FlexDiv>
                        <TextField
                            size="small"
                            label="Text"
                            value={link.text || ''}
                            onChange={(e) =>
                                patchLink(index, { text: e.target.value })
                            }
                            fullWidth
                        />
                        <TextField
                            size="small"
                            label="URL (href)"
                            value={link.href || ''}
                            onChange={(e) =>
                                patchLink(index, { href: e.target.value })
                            }
                            fullWidth
                        />
                        <TextField
                            size="small"
                            label="clickActionId"
                            value={link.clickActionId || ''}
                            onChange={(e) =>
                                patchLink(index, { clickActionId: e.target.value })
                            }
                            fullWidth
                        />
                        <FormControl size="small" fullWidth>
                            <InputLabel>Type</InputLabel>
                            <Select
                                label="Type"
                                value={link.type || 'button'}
                                onChange={(e) =>
                                    patchLink(index, { type: e.target.value })
                                }
                            >
                                {LINK_TYPES.map((t) => (
                                    <MenuItem key={t} value={t}>
                                        {t}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Stack>
                </Paper>
            ))}
            <FlexDiv>
                <IconButton size="small" onClick={addLink} aria-label="Add link">
                    <AddIcon />
                </IconButton>
                <TextDefault lighter>Add link</TextDefault>
            </FlexDiv>
        </FlexDiv>
    );
}
